import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, TrendingDown } from 'lucide-react';
import MLBPlayerAvatar, { positionColors } from '@/components/mlb-beta/MLBPlayerAvatar';
import MLBPlayerRow from '@/components/mlb-beta/MLBPlayerRow';

interface PlayerStats {
  strikeouts: number;
  errors: number;
  gidp: number;
  caught_stealing: number;
  earned_runs: number;
  walks_allowed: number;
  wild_pitches: number;
  blown_saves: number;
  hit_batsmen: number;
  home_runs_allowed: number;
  balks: number;
  passed_balls: number;
}

interface RosterPlayer {
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  slot: string;
  is_starter: boolean;
  total_trash_points: number;
  games_played: number;
  stats: PlayerStats;
}

interface TeamRoster {
  team_id: string;
  team_name: string;
  players: RosterPlayer[];
  total_tp: number;
}

const TRASH_WEIGHTS: { key: keyof PlayerStats; label: string; short: string; weight: number }[] = [
  { key: 'strikeouts', label: 'Strikeouts', short: 'K', weight: 2 },
  { key: 'errors', label: 'Errors', short: 'E', weight: 5 },
  { key: 'gidp', label: 'Double Plays', short: 'GIDP', weight: 3 },
  { key: 'caught_stealing', label: 'Caught Stealing', short: 'CS', weight: 2 },
  { key: 'earned_runs', label: 'Earned Runs', short: 'ER', weight: 3 },
  { key: 'walks_allowed', label: 'Walks', short: 'BB', weight: 1 },
  { key: 'wild_pitches', label: 'Wild Pitches', short: 'WP', weight: 3 },
  { key: 'blown_saves', label: 'Blown Saves', short: 'BS', weight: 8 },
  { key: 'hit_batsmen', label: 'Hit Batsmen', short: 'HBP', weight: 2 },
  { key: 'home_runs_allowed', label: 'HR Allowed', short: 'HRA', weight: 4 },
  { key: 'balks', label: 'Balks', short: 'BK', weight: 5 },
  { key: 'passed_balls', label: 'Passed Balls', short: 'PB', weight: 4 },
];

const emptyStats: PlayerStats = {
  strikeouts: 0, errors: 0, gidp: 0, caught_stealing: 0,
  earned_runs: 0, walks_allowed: 0, wild_pitches: 0, blown_saves: 0,
  hit_batsmen: 0, home_runs_allowed: 0, balks: 0, passed_balls: 0,
};

const MLBBetaRosters = () => {
  const [teams, setTeams] = useState<TeamRoster[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);

  useEffect(() => {
    const fetchRosters = async () => {
      const { data: league } = await supabase
        .from('leagues')
        .select('id')
        .eq('name', 'MLB Beta Test League')
        .eq('sport', 'mlb')
        .single();

      if (!league) { setLoading(false); return; }

      const { data: assignments } = await supabase
        .from('roster_assignments')
        .select('team_id, slot, is_starter, player_id')
        .in('team_id', (await supabase.from('matchups').select('home_team_id').eq('league_id', league.id).limit(100)).data?.map(m => m.home_team_id) || []);

      if (!assignments || assignments.length === 0) { setLoading(false); return; }

      const playerIds = [...new Set(assignments.map(a => a.player_id))];

      // Fetch players, detailed stats, and team names in parallel
      const [playersRes, statsRes, matchupsRes] = await Promise.all([
        supabase.from('players')
          .select('id, full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, headshot_url')
          .in('id', playerIds),
        supabase.from('player_game_stats')
          .select('player_id, trash_points, strikeouts, errors, gidp, caught_stealing, earned_runs, walks_allowed, wild_pitches, blown_saves, hit_batsmen, home_runs_allowed, balks, passed_balls')
          .in('player_id', playerIds),
        supabase.from('matchups')
          .select('home_team_id, home_team_name, away_team_id, away_team_name')
          .eq('league_id', league.id)
          .limit(100),
      ]);

      // Aggregate stats per player
      const statsByPlayer: Record<string, { tp: number; gp: number; stats: PlayerStats }> = {};
      for (const s of statsRes.data || []) {
        if (!statsByPlayer[s.player_id]) statsByPlayer[s.player_id] = { tp: 0, gp: 0, stats: { ...emptyStats } };
        const rec = statsByPlayer[s.player_id];
        rec.tp += Number(s.trash_points);
        rec.gp++;
        rec.stats.strikeouts += s.strikeouts;
        rec.stats.errors += s.errors;
        rec.stats.gidp += s.gidp;
        rec.stats.caught_stealing += s.caught_stealing;
        rec.stats.earned_runs += s.earned_runs;
        rec.stats.walks_allowed += s.walks_allowed;
        rec.stats.wild_pitches += s.wild_pitches;
        rec.stats.blown_saves += s.blown_saves;
        rec.stats.hit_batsmen += s.hit_batsmen;
        rec.stats.home_runs_allowed += s.home_runs_allowed;
        rec.stats.balks += s.balks;
        rec.stats.passed_balls += s.passed_balls;
      }

      const playerMap = new Map((playersRes.data || []).map(p => [p.id, p]));

      const teamNames: Record<string, string> = {};
      for (const m of matchupsRes.data || []) {
        if (m.home_team_name) teamNames[m.home_team_id] = m.home_team_name;
        if (m.away_team_name) teamNames[m.away_team_id] = m.away_team_name;
      }

      const teamMap: Record<string, RosterPlayer[]> = {};
      for (const a of assignments) {
        const p = playerMap.get(a.player_id);
        if (!p) continue;
        if (!teamMap[a.team_id]) teamMap[a.team_id] = [];
        const s = statsByPlayer[a.player_id] || { tp: 0, gp: 0, stats: { ...emptyStats } };
        teamMap[a.team_id].push({
          player_id: a.player_id,
          full_name: p.full_name,
          position_abbreviation: p.position_abbreviation,
          position_eligibility: p.position_eligibility || null,
          mlb_team_abbreviation: p.mlb_team_abbreviation,
          headshot_url: p.headshot_url,
          slot: a.slot,
          is_starter: a.is_starter,
          total_trash_points: Math.round(s.tp * 10) / 10,
          games_played: s.gp,
          stats: s.stats,
        });
      }

      const result: TeamRoster[] = Object.entries(teamMap).map(([tid, roster]) => ({
        team_id: tid,
        team_name: teamNames[tid] || 'Unknown',
        players: roster.sort((a, b) => {
          if (a.is_starter !== b.is_starter) return a.is_starter ? -1 : 1;
          return b.total_trash_points - a.total_trash_points;
        }),
        total_tp: Math.round(roster.reduce((sum, p) => sum + p.total_trash_points, 0) * 10) / 10,
      })).sort((a, b) => b.total_tp - a.total_tp);

      setTeams(result);
      setExpandedTeam(result[0]?.team_id || null);
      setLoading(false);
    };

    fetchRosters();
  }, []);

  if (loading) {
    return (
      <div className="space-y-3 pb-20 md:pb-0">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="font-medium text-foreground">No rosters found</p>
          <p className="text-sm mt-1">Teams haven't been seeded yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground">Team Rosters</h1>
        <Badge variant="secondary" className="text-xs">{teams.length} teams</Badge>
      </div>

      <div className="space-y-3">
        {teams.map((team) => {
          const isExpanded = expandedTeam === team.team_id;
          const starters = team.players.filter(p => p.is_starter);
          const bench = team.players.filter(p => !p.is_starter);

          return (
            <Card key={team.team_id} className="bg-card border-border overflow-hidden">
              <button
                onClick={() => setExpandedTeam(isExpanded ? null : team.team_id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-base font-bold font-heading text-foreground">{team.team_name}</span>
                  <Badge variant="outline" className="text-[10px]">{team.players.length} players</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-primary font-bold text-sm">
                    <TrendingDown className="w-3.5 h-3.5" />
                    {team.total_tp} TP
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
              </button>

              {isExpanded && (
                <CardContent className="p-0 border-t border-border">
                  <div className="px-4 py-1.5 bg-muted/30">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Starters</span>
                  </div>
                  {starters.map((player, i) => (
                    <PlayerRowCompact
                      key={`${player.player_id}-${i}`}
                      player={player}
                      isExpanded={expandedPlayer === player.player_id}
                      onToggle={() => setExpandedPlayer(expandedPlayer === player.player_id ? null : player.player_id)}
                    />
                  ))}

                  {bench.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 bg-muted/30 border-t border-border">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bench</span>
                      </div>
                      {bench.map((player, i) => (
                        <PlayerRowCompact
                          key={`bench-${player.player_id}-${i}`}
                          player={player}
                          isExpanded={expandedPlayer === player.player_id}
                          onToggle={() => setExpandedPlayer(expandedPlayer === player.player_id ? null : player.player_id)}
                        />
                      ))}
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const PlayerRowCompact = ({ player, isExpanded, onToggle }: { player: RosterPlayer; isExpanded: boolean; onToggle: () => void }) => {
  const activeStats = TRASH_WEIGHTS.filter(tw => player.stats[tw.key] > 0);
  const slotLabel = player.slot === 'BENCH' ? 'BN' : player.slot;

  return (
    <div className="border-t border-border/30">
      <MLBPlayerRow
        playerId={player.player_id}
        name={player.full_name}
        position={player.position_abbreviation}
        positionEligibility={player.position_eligibility || undefined}
        team={player.mlb_team_abbreviation}
        headshotUrl={player.headshot_url}
        slot={slotLabel}
        extraInfo={player.games_played > 0 ? `• ${player.games_played} GP` : undefined}
        statValue={player.total_trash_points}
        statLabel="TP"
        isStarter={player.is_starter}
        onClick={onToggle}
        className="hover:bg-muted/20"
        actions={
          player.games_played > 0 ? (
            <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground transition-transform", isExpanded && "rotate-180")} />
          ) : undefined
        }
      />

      {/* Expanded stat breakdown */}
      {isExpanded && player.games_played > 0 && (
        <div className="px-4 pb-3 pt-0">
          <div className="rounded-md bg-muted/40 border border-border/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Trash Stat Breakdown</span>
              <span className="text-[10px] text-muted-foreground">{player.games_played} game{player.games_played !== 1 ? 's' : ''}</span>
            </div>

            {activeStats.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {activeStats.map(tw => {
                  const count = player.stats[tw.key];
                  const tp = count * tw.weight;
                  return (
                    <div key={tw.key} className="flex flex-col items-center rounded bg-background/60 border border-border/30 px-2 py-1.5">
                      <span className="text-lg font-bold text-foreground">{count}</span>
                      <span className="text-[10px] font-semibold text-muted-foreground">{tw.short}</span>
                      <span className="text-[9px] text-primary font-medium mt-0.5">+{tp} TP</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">No trash stats recorded yet</p>
            )}

            {/* Full breakdown table for all stats */}
            {activeStats.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border/30">
                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                  {TRASH_WEIGHTS.map(tw => {
                    const count = player.stats[tw.key];
                    if (count === 0) return null;
                    return (
                      <span key={tw.key} className="text-[10px] text-muted-foreground">
                        {tw.label}: <span className="text-foreground font-medium">{count}</span>
                        <span className="text-primary"> × {tw.weight} = {count * tw.weight}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MLBBetaRosters;
